import {authorizationHeaderFromState} from '../auth'
import {SERVER_URL} from '../config'
import {AppState} from '../store/store'
import {SponsorImageKey} from '../types/lessonKeys'
import {
  CommunitySponsorAssignments,
  LessonSponsorAssignment,
  ModuleSponsorAssignment,
  NationalSponsorAssignments
} from '../types/sponsorAssignments'

const ALL_LESSON_KEYS: SponsorImageKey[] = [
  'MODULE_ONE',
  'LESSON_ONE_ONE_START',
  'LESSON_ONE_ONE_COMPLETION',
  'LESSON_ONE_TWO_START',
  'LESSON_ONE_TWO_COMPLETION',
  'MODULE_TWO',
  'LESSON_TWO_ONE_START',
  'LESSON_TWO_ONE_COMPLETION',
  'LESSON_TWO_TWO_START',
  'LESSON_TWO_TWO_COMPLETION',
  'LESSON_TWO_THREE_START',
  'LESSON_TWO_THREE_COMPLETION',
  'LESSON_TWO_FOUR_START',
  'LESSON_TWO_FOUR_COMPLETION',
  'LESSON_TWO_FIVE_START',
  'LESSON_TWO_FIVE_COMPLETION',
  'LESSON_TWO_SIX_START',
  'LESSON_TWO_SIX_COMPLETION',
  'LESSON_TWO_SEVEN_START',
  'LESSON_TWO_SEVEN_COMPLETION',
  'LESSON_TWO_EIGHT_START',
  'LESSON_TWO_EIGHT_COMPLETION',
  'LESSON_TWO_NINE_START',
  'LESSON_TWO_NINE_COMPLETION',
  'LESSON_TWO_TEN_START',
  'LESSON_TWO_TEN_COMPLETION',
  'LESSON_TWO_ELEVEN_START',
  'LESSON_TWO_ELEVEN_COMPLETION',
  'MODULE_THREE',
  'LESSON_THREE_ONE_START',
  'LESSON_THREE_ONE_COMPLETION',
  'LESSON_THREE_TWO_START',
  'LESSON_THREE_TWO_COMPLETION',
  'LESSON_THREE_THREE_START',
  'LESSON_THREE_THREE_COMPLETION',
  'LESSON_THREE_FOUR_START',
  'LESSON_THREE_FOUR_COMPLETION',
  'LESSON_THREE_FIVE_START',
  'LESSON_THREE_FIVE_COMPLETION',
  'MODULE_FOUR',
  'LESSON_FOUR_ONE_START',
  'LESSON_FOUR_ONE_COMPLETION',
  'LESSON_FOUR_TWO_START',
  'LESSON_FOUR_TWO_COMPLETION',
  'LESSON_FOUR_THREE_START',
  'LESSON_FOUR_THREE_COMPLETION'
]

const ALL_MODULE_KEYS: SponsorImageKey[] = [
  'MODULE_ONE',
  'MODULE_TWO',
  'MODULE_THREE',
  'MODULE_FOUR'
]

export async function fetchCommunitySponsorAssignments(
  communityId: string,
  state: AppState
): Promise<CommunitySponsorAssignments> {
  const url = new URL(
    `/business/communities/${communityId}/communitySponsors`,
    SERVER_URL
  )
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...authorizationHeaderFromState(state)
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch sponsor assignments: ${response.status}`)
  }

  const assignmentMap: Record<string, string | null> = await response.json()

  // Convert the map back to the array format expected by the client
  const assignments = ALL_LESSON_KEYS.map(key => ({
    lessonKey: key,
    sponsorId: assignmentMap[key] || null
  }))

  return {
    communityId,
    assignments
  }
}

export async function updateSponsorAssignments(
  communityId: string,
  assignments: LessonSponsorAssignment[],
  state: AppState
): Promise<void> {
  // Convert assignments array to map format for the API, excluding module keys
  const assignmentMap: Record<string, string | null> = {}

  assignments.forEach(assignment => {
    // Only send lesson keys, not module keys
    if (!ALL_MODULE_KEYS.includes(assignment.lessonKey)) {
      assignmentMap[assignment.lessonKey] = assignment.sponsorId
    }
  })

  const url = new URL(
    `/business/communities/${communityId}/communitySponsors`,
    SERVER_URL
  )
  const response = await fetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authorizationHeaderFromState(state)
    },
    body: JSON.stringify({assignments: assignmentMap})
  })

  if (!response.ok) {
    throw new Error(`Failed to update sponsor assignments: ${response.status}`)
  }
}

export async function updateNationalSponsorAssignments(
  assignments: ModuleSponsorAssignment[],
  state: AppState
): Promise<void> {
  // Convert module assignments to the format expected by the API
  const assignmentMap: Record<string, string | null> = {}

  assignments.forEach(assignment => {
    assignmentMap[assignment.moduleKey] = assignment.sponsorId
  })

  // Wrap in the expected request structure
  const requestBody = {
    assignments: assignmentMap
  }

  const url = new URL('/business/nationalSponsors', SERVER_URL)
  const response = await fetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authorizationHeaderFromState(state)
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    throw new Error(
      `Failed to update national sponsor assignments: ${response.status}`
    )
  }
}

export async function fetchNationalSponsorAssignments(
  state: AppState
): Promise<NationalSponsorAssignments> {
  // For national sponsors, we need to fetch assignments from all communities
  // and aggregate the module assignments
  const url = new URL('/business/nationalSponsors', SERVER_URL)
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...authorizationHeaderFromState(state)
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch communities: ${response.status}`)
  }
  const data = await response.json()

  // Initialize module assignments with only valid module keys
  const moduleAssignments = ALL_MODULE_KEYS.map(moduleKey => ({
    moduleKey: moduleKey as SponsorImageKey,
    sponsorId: data[moduleKey] || null
  }))

  return {
    assignments: moduleAssignments
  }
}
